import type { CustomerRecord } from '@/domain/types';
import { dmSendMode, lastRealInbound } from '@/domain/dmSendMode';

/**
 * The Meta 24-hour messaging window, expressed as the Messenger inbox's working
 * lifecycle. A thread is `needs-reply` while the client holds the last word,
 * `awaiting` once we've replied, `closing-soon` in the final hours before the
 * window shuts, and `elapsed` once it has (after which you can no longer message
 * on Meta — the thread auto-resolves to Nurture or Ops, handled elsewhere).
 *
 * The window is anchored to the most recent *inbound* (client) message, because
 * on Meta only a client message resets the 24h clock — our replies never do.
 */
export type MessengerPhase = 'needs-reply' | 'closing-soon' | 'awaiting' | 'elapsed';

export interface MessengerWindow {
  /** Hours left before the 24h window closes; negative once elapsed. */
  hoursLeft: number;
  /** Who sent the most recent message in the thread. */
  lastBy: 'client' | 'us';
  phase: MessengerPhase;
  /**
   * Real HookChat threads only (tv-isee): hours left in the 24h–7d human-agent
   * window, when the thread is in it. The 24h window has closed but an agent can
   * still send, so the phase stays needs-reply/awaiting rather than elapsed.
   */
  agentHoursLeft?: number;
}

const WINDOW_MS = 24 * 60 * 60 * 1000;
const HOUR_MS = 60 * 60 * 1000;
/** Threshold (hours) at which a still-open thread flips to "closing soon". */
export const CLOSING_SOON_HOURS = 5;

/**
 * The "now" a thread's window is measured against (tv-isee). A thread with a real
 * HookChat inbound (lastRealInbound, the same anchor as dmSendMode) runs on WALL
 * time: Meta's windows are real time, and a dev/staging sim clock parked in May
 * would put a real September DM months "in the future". Simulated/demo threads
 * keep the sim clock, unchanged.
 */
export function windowNowIso(customer: Pick<CustomerRecord, 'conversation'>, simClockIso: string): string {
  return lastRealInbound(customer) ? new Date().toISOString() : simClockIso;
}

/** `simClockIso` is the app's sim clock; HookChat threads swap in wall time (see windowNowIso). */
export function messengerWindow(customer: CustomerRecord, simClockIso: string): MessengerWindow {
  const convo = customer.conversation ?? [];
  const now = new Date(windowNowIso(customer, simClockIso)).getTime();
  const last = convo[convo.length - 1];
  // B6: an automated text (a receipt, the waiver, a reminder — authorRole
  // 'system') is not a reply to the client: it never hides "needs reply".
  const lastHuman = [...convo].reverse().find((e) => !(e.direction === 'out' && e.authorRole === 'system'));
  const lastBy: 'client' | 'us' = lastHuman?.direction === 'out' ? 'us' : 'client';

  // Only a client (inbound) message resets the Meta window; fall back to the
  // last entry, then to `now`, so an empty thread never produces NaN. A real
  // HookChat thread anchors on its last HookChat inbound, like dmSendMode.
  const real = lastRealInbound(customer);
  const lastInbound = real ?? [...convo].reverse().find((e) => e.direction === 'in');
  const anchor = lastInbound
    ? new Date(lastInbound.timestamp).getTime()
    : last
      ? new Date(last.timestamp).getTime()
      : now;

  const hoursLeft = (anchor + WINDOW_MS - now) / HOUR_MS;

  if (real && hoursLeft <= 0) {
    const dm = dmSendMode(customer, 'ig-dm', new Date(now).toISOString());
    if (dm.mode === 'human_agent') {
      return { hoursLeft, lastBy, phase: lastBy === 'client' ? 'needs-reply' : 'awaiting', agentHoursLeft: dm.hoursLeft };
    }
  }

  let phase: MessengerPhase;
  if (hoursLeft <= 0) phase = 'elapsed';
  else if (hoursLeft <= CLOSING_SOON_HOURS) phase = 'closing-soon';
  else if (lastBy === 'client') phase = 'needs-reply';
  else phase = 'awaiting';

  return { hoursLeft, lastBy, phase };
}

/** The Messenger's channels: Instagram and Facebook DMs. */
export const MESSENGER_CHANNELS: ReadonlySet<string> = new Set(['ig-dm', 'meta-dm']);

/**
 * 7q73: is this an Instagram/Facebook thread for the Messenger inbox? Decided by
 * the latest HUMAN entry (an automated server text — a receipt, the waiver, a
 * reminder, B6 — is skipped), so a server SMS after a DM no longer drops the
 * thread out of the inbox.
 */
export function isMessengerThread(customer: Pick<CustomerRecord, 'conversation'>): boolean {
  const convo = customer.conversation ?? [];
  const lastHuman = [...convo].reverse().find((e) => !(e.direction === 'out' && e.authorRole === 'system'));
  return lastHuman !== undefined && MESSENGER_CHANNELS.has(lastHuman.channel);
}

/**
 * 7q73: "Needs reply" = the client holds the last word, whatever the window
 * says. A client-last thread in its final hours (closing soon) or past 24h still
 * needs a reply; the phase chip shows how urgent it is.
 */
export function needsReply(win: Pick<MessengerWindow, 'lastBy'>): boolean {
  return win.lastBy === 'client';
}
